import React from 'react';
import { useLocation, Navigate, Routes, Route } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import PageLayout from './PageLayout';
import SignUpContainer from '../Auth/SignUp/SignUpContainer';
import Profile from '../Profile/Profile';
import BirthDateStep from '../Auth/SignUp/BirthDateStep';
import ProfileSetupStep from '../Auth/SignUp/ProfileSetupStep';
import QueueJoinHandler from '../Queue/QueueJoinHandler';
import Redemption from '../Redemption/Redemption';
import RedemptionSuccess from '../Redemption/RedemptionSuccess';
import { useLoading } from '../../contexts/LoadingContext';
import apiClient from '../../utils/apiClient';

const ProtectedRoute = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = React.useState(null);
    const [needsProfileSetup, setNeedsProfileSetup] = React.useState(false);
    const [needsBirthDate, setNeedsBirthDate] = React.useState(false);
    const [userEmail, setUserEmail] = React.useState('');
    const { startLoading, stopLoading } = useLoading();
    const location = useLocation();

    React.useEffect(() => {
        const verifyAuth = async () => {
            startLoading();
            try {
                const response = await apiClient.get('/api/users/profile/');
                setIsAuthenticated(true);
                setNeedsProfileSetup(!response.data.profile_setup_complete);
                setNeedsBirthDate(!response.data.date_of_birth);
                setUserEmail(response.data.email);
            } catch (error) {
                setIsAuthenticated(false);
            } finally {
                stopLoading();
            }
        };
        verifyAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleBirthDateComplete = () => {
        setNeedsBirthDate(false);
    };

    const handleProfileSetupComplete = () => {
        setNeedsProfileSetup(false);
    };

    // Wait for auth check
    if (isAuthenticated === null) return null;
    if (!isAuthenticated) {
        // Preserve the current URL and search params for redirect after login
        const redirectUrl = `${location.pathname}${location.search}`;
        return <Navigate to={`/login?redirect=${encodeURIComponent(redirectUrl)}`} replace />;
    }

    return (
        <PageLayout>
            <AnimatePresence mode="popLayout">
                {needsProfileSetup ? (
                    needsBirthDate ? (
                        <motion.div
                            key="birthdate-setup"
                            initial={{ x: 300, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: -300, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        >
                            <BirthDateStep 
                                email={userEmail}
                                onSuccess={handleBirthDateComplete} 
                            />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="profile-setup"
                            initial={{ x: 300, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: -300, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        >
                            <ProfileSetupStep onSubmit={handleProfileSetupComplete} />
                        </motion.div>
                    )
                ) : (
                    <motion.div key="profile" style={{ position: 'relative', width: '100%' }}>
                        {children}
                    </motion.div>
                )}
            </AnimatePresence>
        </PageLayout>
    );
};

const AnimatedRoutes = () => {
    const location = useLocation();

    return (
        <div>
            <Routes location={location}>
                <Route path="/login" element={<SignUpContainer initialStep="login" />} />
                <Route path="/signup" element={<SignUpContainer initialStep="email" />} />
                <Route path="/reset-password" element={<SignUpContainer initialStep="new-password" />} />
                <Route path="/auth/google/callback" element={<SignUpContainer initialStep="login" />} />
                {/* No direct /profile-setup route, only via ProtectedRoute */}
                <Route 
                    path="/queues/:code/join" 
                    element={
                        <ProtectedRoute>
                            <QueueJoinHandler />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="/redemption" 
                    element={
                        <ProtectedRoute>
                            <Redemption />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="/redemption/success" 
                    element={
                        <ProtectedRoute>
                            <RedemptionSuccess />
                        </ProtectedRoute>
                    } 
                />
                <Route
                    path="/profile"
                    element={
                        <ProtectedRoute>
                            <Profile />
                        </ProtectedRoute>
                    }
                />
                <Route path="/" element={<Navigate to="/profile" replace />} />
            </Routes>
        </div>
    );
};

export default AnimatedRoutes;
